import axios from 'axios';
import { showToast } from '#root/components/GlobalToaster';
import { offlineOperationsService } from '#root/services/offlineOperationsService';
import { OFFLINE_MESSAGES } from './offline';
import { executeOperation, Operations } from './offlineOperationHandlers';
import { OfflineOperationType } from '#root/utils/offlineOperations';

interface OfflineWrapperOptions<T> {
    operationType: OfflineOperationType;
    operationData: any;
    operations: Operations;
    metadata?: Record<string, any>;
    onSuccess?: (result: T) => void;
    onQueued?: () => void;
    successMessage?: string;
}

/**
 * Wraps an async operation with offline handling logic.
 * If offline or network error occurs, queues the operation for later.
 * Otherwise executes it immediately.
 */
export const withOfflineSupport = async <T,>({
    operationType,
    operationData,
    operations,
    metadata,
    onSuccess,
    onQueued,
    successMessage
}: OfflineWrapperOptions<T>): Promise<T | void> => {
    try {
        // If we're offline, queue the operation immediately
        if (!navigator.onLine) {
            await offlineOperationsService.queueOperation(operationType, operationData, metadata);
            showToast(OFFLINE_MESSAGES.REQUEST_QUEUED, 'info');
            onQueued?.();
            return;
        }

        // If we're online, proceed normally
        const result = await executeOperation(operationType, operationData, operations);
        if (successMessage) {
            showToast(successMessage, 'success');
        }
        onSuccess?.(result);
        return result;
    } catch (error) {
        // If network error occurs, try to queue the operation
        if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
            try {
            await offlineOperationsService.queueOperation(operationType, operationData, metadata);
            showToast(OFFLINE_MESSAGES.REQUEST_QUEUED, 'info');
            onQueued?.();
            return;
            } catch (queueError) {
                showToast(OFFLINE_MESSAGES.FAILED_TO_QUEUE, 'error');
                console.error('Error queueing operation:', queueError);
                throw queueError;
            }
        }
        
        // For other errors, show error message and rethrow
        //showToast(OFFLINE_MESSAGES.SOMETHING_WENT_WRONG, 'error');
        console.error('Operation failed:', error);
        throw error;
    }
};
